import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Toggle from "@amzn/awsui-components-react/polaris/toggle";
import Autosuggest from "@amzn/awsui-components-react/polaris/autosuggest";
import Select from "@amzn/awsui-components-react/polaris/select";
import DatePicker from "@amzn/awsui-components-react/polaris/date-picker";
import type { PillPackPrescriptionDetail } from "@amzn/apaqportaljavalambdatypescriptclient";

export interface MedicationOption {
  drugId: string;
  description?: string;
}

export interface PrescriptionFormState extends PillPackPrescriptionDetail {
  drugIdInput: string;
}

interface Props {
  prescription: PrescriptionFormState;
  medications: MedicationOption[];
  onChange: (updates: Partial<PrescriptionFormState>) => void;
  onReset: () => void;
}

class PrescriptionDetailsBlock extends React.Component<Props> {
  render() {
    const { prescription, medications, onChange, onReset } = this.props;

    const filteredOptions = medications
      .filter((m) =>
        !prescription.drugIdInput ||
        m.drugId.toLowerCase().includes(prescription.drugIdInput.toLowerCase()) ||
        (m.description || "").toLowerCase().includes(prescription.drugIdInput.toLowerCase())
      )
      .map((m) => ({ value: m.drugId, label: `${m.description || m.drugId} (${m.drugId})` }));

    return (
      <Container header={<Header variant="h3" actions={<Button onClick={onReset}>Reset</Button>}>Prescription Details</Header>}>
        <SpaceBetween size="m">
          <FormField label="Drug" description="Required">
            <Autosuggest
              value={prescription.drugIdInput || prescription.drugId || ""}
              onChange={({ detail }) => onChange({ drugIdInput: detail.value, drugId: "" })}
              onSelect={({ detail }) => {
                const med = medications.find((m) => m.drugId === detail.value);
                onChange({ drugId: detail.value, drugIdInput: med ? `${med.description} (${detail.value})` : detail.value });
              }}
              options={filteredOptions}
              placeholder="Search medications..."
              empty="No matching medications"
            />
          </FormField>

          <ColumnLayout columns={3}>
            <FormField label="Initial Refills">
              <Input type="number" value={prescription.initialRefills || ""} onChange={({ detail }) => onChange({ initialRefills: detail.value })} />
            </FormField>
            <FormField label="Quantity Authorized">
              <Input type="number" value={prescription.quantityAuthorized || ""} onChange={({ detail }) => onChange({ quantityAuthorized: detail.value })} />
            </FormField>
            <FormField label="Days Supply">
              <Input type="number" value={prescription.daysSupply || ""} onChange={({ detail }) => onChange({ daysSupply: detail.value })} />
            </FormField>
            <FormField label="Dispense Quantity">
              <Input type="number" value={prescription.dispenseQuantity || ""} onChange={({ detail }) => onChange({ dispenseQuantity: detail.value })} />
            </FormField>
            <FormField label="Dispense Days Supply">
              <Input type="number" value={prescription.dispenseDaysSupply || ""} onChange={({ detail }) => onChange({ dispenseDaysSupply: detail.value })} />
            </FormField>
            <FormField label="Expiration Date">
              <DatePicker
                value={prescription.expirationDate || ""}
                onChange={({ detail }) => onChange({ expirationDate: detail.value })}
                placeholder="YYYY/MM/DD"
              />
            </FormField>
            <FormField label="One Time Fill Reason">
              <Input value={prescription.oneTimeFillReason || ""} onChange={({ detail }) => onChange({ oneTimeFillReason: detail.value })} />
            </FormField>
            <FormField label="Status">
              <Select
                selectedOption={prescription.status ? { value: prescription.status, label: prescription.status } : null}
                onChange={({ detail }) => onChange({ status: detail.selectedOption.value || "" })}
                options={[
                  { value: "", label: "Complete" },
                  { value: "data_entry", label: "data_entry" },
                  { value: "rph_check", label: "rph_check" },
                ]}
                placeholder="Complete (default)"
              />
            </FormField>
          </ColumnLayout>

          <Toggle
            checked={prescription.inPackets || false}
            onChange={({ detail }) => onChange({ inPackets: detail.checked })}
          >
            In Packets
          </Toggle>
        </SpaceBetween>
      </Container>
    );
  }
}

export default PrescriptionDetailsBlock;
